import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowUp,
  Box,
  Plus,
  Paperclip,
  Mic,
  ChevronDown,
  Sparkles,
  ChevronRight,
  Check,
  Send,
  FileText,
  Menu,
  Brain,
  Plug,
  Database,
  Search,
  Puzzle,
  RotateCcw,
  Calculator,
  ShieldCheck,
  Cpu,
  Compass,
  Layers,
  ShoppingBag,
  Wrench
} from 'lucide-react';
import MarkdownRenderer from './openui/MarkdownRenderer';
import AgentToolTasks from './AgentToolTasks';
import dashnodeLogo from '../assets/auth/Dashnode_logo.png';
import dashnodeLogoWithName from '../assets/auth/Dashnode_logo_with_name.png';

const ENGINEER_MAP = {
  cost: { name: 'Costing', tone: 'blue', icon: Calculator, bg: 'var(--blue-tint)', fg: 'var(--blue)' },
  dfm: { name: 'DFM', tone: 'teal', icon: ShieldCheck, bg: 'var(--green-tint)', fg: 'var(--green-ink)' },
  rnd: { name: 'R&D', tone: 'purple', icon: Sparkles, bg: 'var(--purple-tint)', fg: 'var(--purple)' },
  design: { name: 'Design', tone: 'blue', icon: Compass, bg: 'var(--blue-tint)', fg: 'var(--blue)' },
  sim: { name: 'Simulation', tone: 'indigo', icon: Cpu, bg: 'var(--indigo-tint)', fg: 'var(--indigo)' },
  mfg: { name: 'Manufacturing', tone: 'slate', icon: Wrench, bg: 'var(--slate-tint)', fg: 'var(--slate)' },
  qa: { name: 'Quality', tone: 'rose', icon: Layers, bg: 'var(--rose-tint)', fg: 'var(--rose)' },
  proc: { name: 'Procurement', tone: 'amber', icon: ShoppingBag, bg: 'var(--amber-tint)', fg: 'var(--amber)' }
};

const EXAMPLES = [
  {
    id: 'smr',
    title: 'SMR core wall redesign',
    tone: 'purple',
    icon: Sparkles,
    q: 'New SMR core wall for Gen-3 — it runs about 80 °C hotter. Redesign from our current spec, confirm it holds, and give me fab-ready drawings for our suppliers.',
    chain: ['R&D', 'Design', 'Simulation', 'DFM', 'Procurement']
  },
  {
    id: 'tradeoff',
    title: 'Shield-wall trade study',
    tone: 'indigo',
    icon: Cpu,
    q: 'We want the shield wall 15% thinner. What does that force?',
    chain: ['R&D', 'Simulation', 'Design', 'Costing']
  },
  {
    id: 'cost',
    title: 'Assembly shoot cost',
    tone: 'blue',
    icon: Calculator,
    q: 'Cost the full control-rod drive assembly at 50 units — every component and sub-assembly, and flag what we should make versus buy.',
    chain: ['Costing', 'DFM', 'Procurement']
  }
];

export default function ChatInterface({
  activeChat,
  onSendMessage,
  isGenerating,
  isMemoryOpen,
  onToggleMemory,
  memoryCount = 0,
  currentUser = null,
  onRequireAuth,
  onOpenAuth,
  onOpenConnectors,
  onToggleRail,
  onBackHome,
  onSelectExampleTask
}) {
  const [inputText, setInputText] = useState('');
  const [customFile, setCustomFile] = useState(undefined);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const followUpTextareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const plusMenuRef = useRef(null);

  const hasMessages = activeChat?.messages && activeChat.messages.length > 0;
  const isHome = !hasMessages && (activeChat?.id === 'new' || !activeChat?.id);

  const attachedFile = customFile !== undefined
    ? customFile
    : (activeChat?.fileName ? { name: activeChat.fileName, size: activeChat.fileSize } : null);

  // Close plus context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target)) {
        setIsPlusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus on home / new
  useEffect(() => {
    if (isHome && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isHome, activeChat?.id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (hasMessages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages, isGenerating, hasMessages]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 160)}px`;
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if ((!inputText.trim() && !attachedFile) || isGenerating) return;

    const defaultPrompt = attachedFile
      ? `Analyze ${attachedFile.name} for manufacturing feasibility, cost breakdown, and cycle time.`
      : '';

    const finalPrompt = inputText.trim() || defaultPrompt;
    if (!finalPrompt) return;

    const payload = {
      text: finalPrompt,
      attachedFile
    };

    if (!currentUser && onRequireAuth) {
      onRequireAuth({
        ...payload,
        onSuccess: () => {
          setInputText('');
          if (textareaRef.current) textareaRef.current.style.height = 'auto';
          if (followUpTextareaRef.current) followUpTextareaRef.current.style.height = 'auto';
        }
      });
      return;
    }

    onSendMessage(payload);
    setInputText('');
    setCustomFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (followUpTextareaRef.current) followUpTextareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const ext = file.name.split('.').pop()?.toUpperCase() || 'CAD';
      setCustomFile({
        name: file.name,
        size: `${ext} · ${sizeMB} MB`,
        rawFile: file
      });
    }
    setIsPlusMenuOpen(false);
  };

  const handleRemoveFile = () => {
    setCustomFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const userInitial = currentUser?.name?.charAt(0).toUpperCase() || 'RA';

  // -------------------------------------------------------------
  // GUEST VIEW (WHEN NOT LOGGED IN): Core page just like before
  // -------------------------------------------------------------
  const renderGuestComposer = () => (
    <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200/90 shadow-lg shadow-slate-200/40 p-3 space-y-2.5 transition-all focus-within:border-blue-500/70 focus-within:ring-4 focus-within:ring-blue-500/10 hover:border-slate-300">
      {/* File Attachment Chip */}
      {attachedFile && (
        <div className="inline-flex items-center gap-2 bg-blue-50/90 border border-blue-200 px-2 py-0.5 rounded-md text-xs">
          <Box className="w-3 h-3 text-blue-600 shrink-0" />
          <span className="font-semibold text-slate-900 text-[11px] truncate max-w-[160px]">
            {attachedFile.name}
          </span>
          <span className="text-[10px] text-slate-500 font-mono">
            {attachedFile.size}
          </span>
          <button
            type="button"
            onClick={handleRemoveFile}
            className="text-slate-400 hover:text-slate-700 ml-1 text-xs font-bold hover:bg-blue-100 rounded px-1 transition-colors cursor-pointer"
            title="Remove file"
          >
            ×
          </button>
        </div>
      )}

      {/* Compact Textarea Input */}
      <textarea
        ref={textareaRef}
        rows={1}
        value={inputText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Enter an engineering inquiry, specify material tolerances, or attach a CAD file..."
        className="w-full text-xs sm:text-sm text-slate-800 placeholder-slate-400 border-none outline-none resize-none pt-1 leading-relaxed min-h-[36px]"
      />

      {/* Compact Footer Bar: + Attach button, Memory button, and Send button */}
      <div className="flex items-center justify-between pt-1 border-t border-slate-100">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-2 py-1 text-slate-500 hover:text-slate-800 rounded-md hover:bg-slate-100 transition-colors cursor-pointer flex items-center gap-1 text-xs"
            title="Attach CAD / STEP file"
          >
            <Plus className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[11px] font-medium text-slate-600">Attach STEP/CAD</span>
          </button>

          <button
            type="button"
            onClick={onToggleMemory}
            className={`px-2 py-1 rounded-md transition-colors cursor-pointer flex items-center gap-1.5 text-xs ${
              isMemoryOpen
                ? 'bg-blue-100 text-blue-800 font-semibold shadow-2xs'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
            title="Open memory statements"
          >
            <Brain className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[11px] font-medium">Memory</span>
            <span className="px-1.5 py-0.2 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold border border-blue-200">
              {memoryCount}
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isGenerating || (!inputText.trim() && !attachedFile)}
            className="w-8 h-8 rounded-full bg-[#0C64E8] hover:bg-[#0A4FBB] disabled:opacity-30 disabled:hover:bg-[#0C64E8] text-white flex items-center justify-center shadow-md shadow-blue-500/20 transition-all active:scale-95 cursor-pointer"
            title="Send message"
          >
            <ArrowUp className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>
      </div>
    </div>
  );

  if (!currentUser) {
    return (
      <main className="flex-1 flex flex-col h-full bg-slate-50/60 overflow-hidden relative font-sans">
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
          accept=".step,.stp,.iges,.igs,.dxf,.sldprt,.dwg,.pdf"
        />

        {/* Guest Top Navbar (Clean brand header when no user is signed in) */}
        <header className="h-14 px-4 sm:px-8 border-b border-slate-200/80 bg-white/80 backdrop-blur-md flex items-center justify-between shrink-0 z-10 select-none">
          <div className="flex items-center gap-3">
            <img
              src={dashnodeLogoWithName}
              alt="Dashnode AI"
              className="h-6 sm:h-7 w-auto object-contain"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenAuth?.('login')}
              className="px-3.5 py-1.5 text-xs font-semibold text-white bg-[#0C64E8] hover:bg-[#0A4FBB] rounded-lg shadow-sm shadow-blue-500/25 transition-all cursor-pointer active:scale-98"
            >
              Sign In
            </button>
          </div>
        </header>

        {/* Guest Core Landing Page (Empty State Centered Hero) */}
        {!hasMessages ? (
          <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 overflow-y-auto relative">
            {/* Ambient Subtle Radial Glow Backdrop */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_75%_50%_at_50%_25%,rgba(37,99,235,0.06),transparent_70%)]" />

            <div className="w-full max-w-2xl space-y-6 -mt-8 relative z-1 animate-in fade-in zoom-in-95 duration-200">
              {/* Premium Centered Brand Emblem & Heading */}
              <div className="text-center space-y-3">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-xl shadow-blue-500/10 border border-slate-200/90 flex items-center justify-center p-3 mx-auto transition-transform hover:scale-105 duration-300">
                  <img src={dashnodeLogo} alt="Dashnode AI" className="w-full h-full object-contain" />
                </div>

                <div className="space-y-1.5 pt-1">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold tracking-wide border border-blue-200/60 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0C64E8] animate-pulse" />
                    <span>Autonomous Manufacturing Copilot</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
                    What are we engineering today?
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
                    Instant CNC costing, automated DFM geometry checks, and 2D drawing reconciliation.
                  </p>
                </div>
              </div>

              {/* Centered Composer Box */}
              {renderGuestComposer()}
            </div>
          </div>
        ) : (
          /* Guest Active Conversation (if user previously had messages before logging out) */
          <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-5 space-y-5">
            {activeChat?.messages?.map((msg, idx) => {
              const isUser = msg.sender === 'user';
              return (
                <div key={msg.id || idx} className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className={`p-4 rounded-xl text-sm ${isUser ? 'bg-[#0C64E8] text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                    <MarkdownRenderer content={msg.text} />
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>
    );
  }

  // -------------------------------------------------------------
  // AUTHENTICATED VIEW (LOGGED IN): dashnode-workspace.html Design
  // -------------------------------------------------------------
  return (
    <main className="flex-1 flex flex-col h-full bg-[#F3F6F9] overflow-hidden relative font-sans">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        className="hidden"
        accept=".step,.stp,.iges,.igs,.dxf,.sldprt,.dwg,.pdf"
      />

      {/* 1. TOPBAR */}
      <header className="h-14 flex items-center justify-between gap-3 px-5 bg-white border-b border-slate-200 shrink-0 z-20">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Left Rail Toggle */}
          <button
            type="button"
            onClick={onToggleRail}
            className="w-9 h-9 rounded-[6px] border border-slate-200 bg-white hover:bg-[#EEF2F7] flex items-center justify-center text-[#44546A] transition-colors cursor-pointer shrink-0"
            aria-label="Toggle rail"
          >
            <Menu className="w-4 h-4" />
          </button>

          {/* Navigation Title / Back Button */}
          {hasMessages ? (
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={onBackHome}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] border border-slate-200 bg-white hover:bg-[#EEF2F7] text-xs font-semibold text-[#44546A] transition-colors cursor-pointer shrink-0"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>New</span>
              </button>
              <h2 className="text-sm font-semibold text-slate-900 truncate">
                {activeChat.title || 'Engineering Task'}
              </h2>
            </div>
          ) : (
            <span className="text-sm font-semibold text-slate-900 truncate">
              New workspace
            </span>
          )}
        </div>

        {/* Right Topbar Actions */}
        <div className="flex items-center gap-2.5 shrink-0">
          {/* Company selector chip */}
          <div className="hidden sm:inline-flex items-center gap-2 border border-slate-200 bg-white px-2.5 py-1 rounded-full text-[12.5px] font-medium text-[#44546A] shadow-2xs">
            <span className="w-6 h-6 rounded-full bg-[#0C64E8] text-white flex items-center justify-center text-[11px] font-bold">
              KN
            </span>
            <span>Kaveri Nuclear Systems</span>
            <ChevronDown className="w-3.5 h-3.5 text-[#8193A8]" />
          </div>

          {/* Memory Panel Toggle Button */}
          <button
            type="button"
            onClick={onToggleMemory}
            className={`w-9 h-9 rounded-[6px] border flex items-center justify-center transition-colors cursor-pointer ${
              isMemoryOpen
                ? 'bg-[#EFEAFB] text-[#6D53D6] border-[#6D53D6]/40 shadow-xs'
                : 'bg-white text-[#44546A] border-slate-200 hover:bg-[#EEF2F7]'
            }`}
            title="Toggle Memory Panel"
            aria-label="Memory"
          >
            <Brain className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* 2. CENTER WORKSPACE CONTENT */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
        {isHome ? (
          /* ---------- ZERO STATE / HERO VIEW ---------- */
          <div className="flex flex-col justify-center items-center min-h-full max-w-[760px] mx-auto px-6 py-10 sm:py-14 animate-in fade-in duration-200">
            {/* Spark Greeting */}
            <div className="text-2xl sm:text-[30px] font-semibold tracking-[-0.03em] leading-tight text-slate-900 inline-flex items-center justify-center text-center gap-3 mb-7">
              <span className="text-[#0C64E8] flex items-center justify-center">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 stroke-[2.2]" />
              </span>
              <span>What are we engineering today?</span>
            </div>

            {/* Hero Composer Card */}
            <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-[0_8px_30px_rgba(16,36,66,0.10)] p-4 pt-3.5 transition-all focus-within:border-[#0C64E8] focus-within:ring-4 focus-within:ring-[#0C64E8]/10">
              {/* Attached file chip */}
              {attachedFile && (
                <div className="inline-flex items-center gap-2 bg-[#EFF5FE] border border-[#C6D6EE] px-2.5 py-1 rounded-md text-xs mb-2">
                  <Box className="w-3.5 h-3.5 text-[#0C64E8] shrink-0" />
                  <span className="font-semibold text-slate-900 text-xs truncate max-w-[200px]">
                    {attachedFile.name}
                  </span>
                  <span className="text-[11px] text-slate-500 font-mono">
                    {attachedFile.size}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-slate-400 hover:text-slate-700 ml-1 text-xs font-bold hover:bg-blue-100 rounded px-1 transition-colors"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                rows={2}
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Describe an engineering problem — a redesign, a cost, a trade-off…"
                className="w-full text-sm sm:text-base text-slate-900 placeholder-[#8193A8] border-none outline-none resize-none pt-1 leading-relaxed min-h-[52px]"
              />

              {/* Composer Toolbar */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
                {/* Plus context button */}
                <div className="relative" ref={plusMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsPlusMenuOpen(!isPlusMenuOpen)}
                    className="w-9 h-9 rounded-[10px] border border-slate-200 bg-white hover:bg-[#EEF2F7] flex items-center justify-center text-[#44546A] transition-colors cursor-pointer shadow-2xs"
                    aria-label="Add context"
                  >
                    <Plus className="w-4 h-4 stroke-[2.2]" />
                  </button>

                  {/* Plus Popover Menu */}
                  {isPlusMenuOpen && (
                    <div className="absolute bottom-11 left-0 w-72 bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(16,36,66,0.15)] p-1.5 z-40 text-xs text-slate-800 animate-in fade-in zoom-in-95">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Paperclip className="w-4 h-4 text-[#44546A]" />
                        <span className="flex-1 font-medium">Upload or add files</span>
                        <span className="text-[11px] text-[#8193A8] font-mono">⌘U</span>
                      </button>

                      <div className="h-px bg-slate-100 my-1" />

                      <button
                        type="button"
                        onClick={() => {
                          setIsPlusMenuOpen(false);
                          onOpenConnectors?.();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Plug className="w-4 h-4 text-[#44546A]" />
                        <span className="flex-1 font-medium">Connectors</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#8193A8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsPlusMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Puzzle className="w-4 h-4 text-[#44546A]" />
                        <span className="flex-1 font-medium">Plugins</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsPlusMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Search className="w-4 h-4 text-[#44546A]" />
                        <span className="flex-1 font-medium">Deep research</span>
                      </button>

                      <div className="h-px bg-slate-100 my-1" />

                      <button
                        type="button"
                        onClick={() => {
                          setIsPlusMenuOpen(false);
                          onToggleMemory?.();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Brain className="w-4 h-4 text-[#6D53D6]" />
                        <span className="flex-1 font-medium text-[#6D53D6]">Memory</span>
                        <Check className="w-3.5 h-3.5 text-[#0C64E8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsPlusMenuOpen(false);
                          onOpenConnectors?.();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EEF2F7] text-left transition-colors"
                      >
                        <Database className="w-4 h-4 text-[#44546A]" />
                        <span className="flex-1 font-medium">Legacy data</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#8193A8]" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Right toolbar controls */}
                <div className="flex items-center gap-2.5">
                  {/* Mic Dictate Group */}
                  <div className="flex items-center border border-slate-200 rounded-full overflow-hidden bg-white shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setIsVoiceActive(!isVoiceActive)}
                      className={`w-9 h-8 flex items-center justify-center transition-colors ${
                        isVoiceActive ? 'text-red-600 bg-red-50' : 'text-[#44546A] hover:bg-[#EEF2F7]'
                      }`}
                      title="Dictate prompt"
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="w-6 h-8 border-l border-slate-200 flex items-center justify-center text-[#8193A8] hover:bg-[#EEF2F7]"
                      title="Voice settings"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Send Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isGenerating || (!inputText.trim() && !attachedFile)}
                    className="w-[38px] h-[38px] rounded-full bg-[#0C64E8] hover:bg-[#0A4FBB] disabled:opacity-40 disabled:hover:bg-[#0C64E8] text-white flex items-center justify-center shadow-[0_2px_8px_rgba(12,100,232,0.30)] transition-all active:scale-95 cursor-pointer"
                    title="Send inquiry"
                  >
                    <ArrowUp className="w-4 h-4 stroke-[2.5]" />
                  </button>
                </div>
              </div>
            </div>

            {/* Meta Row below Composer */}
            <div className="w-full flex items-center justify-between mt-3.5 px-1 text-xs text-[#44546A]">
              <div className="inline-flex items-center gap-2 font-medium hover:bg-[#EEF2F7] px-2.5 py-1 rounded-md transition-colors cursor-pointer">
                <span className="w-5 h-5 rounded-full bg-[#0C64E8] text-white flex items-center justify-center text-[10px] font-bold">
                  KN
                </span>
                <span>Kaveri Nuclear Systems</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#8193A8]" />
              </div>

              <div className="inline-flex items-center gap-1.5 text-xs text-[#8193A8]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#11A389]" />
                <span>Memory on</span>
              </div>
            </div>

            {/* Example Queries Section */}
            <div className="w-full mt-10">
              <div className="text-[11.5px] font-bold uppercase tracking-wider text-[#8193A8] text-center mb-3">
                Try one of these
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                {EXAMPLES.map((ex) => {
                  const Icon = ex.icon;
                  const toneColor =
                    ex.tone === 'purple'
                      ? { bg: '#EFEAFB', fg: '#6D53D6' }
                      : ex.tone === 'indigo'
                      ? { bg: '#E8F0FD', fg: '#2E6BD6' }
                      : { bg: '#EFF5FE', fg: '#0C64E8' };

                  return (
                    <button
                      key={ex.id}
                      type="button"
                      onClick={() => onSelectExampleTask?.(ex.id, ex.q)}
                      className="text-left bg-white border border-slate-200 hover:border-[#C6D6EE] hover:shadow-xs rounded-xl p-3 flex flex-col gap-2 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-6 h-6 rounded-[6px] flex items-center justify-center shrink-0"
                          style={{ backgroundColor: toneColor.bg, color: toneColor.fg }}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-[12.5px] font-semibold text-slate-900 truncate">
                          {ex.title}
                        </span>
                      </div>

                      <p className="text-[11.5px] text-[#44546A] leading-relaxed line-clamp-2">
                        {ex.q}
                      </p>

                      <div className="text-[10.5px] text-[#8193A8] truncate mt-auto">
                        {ex.chain.join(' · ')}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* ---------- CONVERSATION VIEW ---------- */
          <div className="max-w-[780px] mx-auto px-6 py-7 pb-28 space-y-6 animate-in fade-in duration-150">
            {activeChat?.messages?.map((msg, idx) => {
              const isUser = msg.sender === 'user';

              if (isUser) {
                return (
                  <div key={msg.id || idx} className="flex gap-3 items-start justify-end">
                    <div className="flex flex-col items-end gap-1.5 max-w-[88%]">
                      {/* Attached CAD Chip */}
                      {msg.file && (
                        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1 rounded-lg text-xs shadow-2xs">
                          <Box className="w-3.5 h-3.5 text-[#0C64E8]" />
                          <span className="font-semibold text-slate-900">{msg.file.name}</span>
                          <span className="text-[11px] text-slate-400 font-mono">
                            {msg.file.size}
                          </span>
                        </div>
                      )}

                      {/* Bubble */}
                      <div className="bg-[#0C64E8] text-white rounded-[14px] rounded-tr-[4px] px-4 py-3 text-[14.5px] leading-relaxed shadow-xs">
                        {msg.text}
                      </div>

                      <span className="text-[10px] text-[#8193A8] px-1 font-mono">
                        {msg.timestamp || 'Just now'}
                      </span>
                    </div>

                    {/* User Avatar */}
                    <div className="w-[30px] h-[30px] rounded-full bg-[#EEF2F7] text-[#44546A] flex items-center justify-center text-xs font-semibold shrink-0 shadow-2xs">
                      {userInitial}
                    </div>
                  </div>
                );
              }

              // Assistant message
              return (
                <div key={msg.id || idx} className="space-y-3 pt-2">
                  {/* Assistant Header */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-[30px] h-[30px] rounded-[8px] bg-[#0C64E8] flex items-center justify-center text-white shrink-0 shadow-[0_2px_6px_rgba(12,100,232,0.30)]">
                      <svg
                        className="w-4 h-4 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="1.7"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      >
                        <path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7z" />
                        <path d="M12 2.5V12M12 12l8.5-5M12 12l-8.5-5M12 12v9.5" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-semibold text-[13.5px] text-slate-900">Dashnode</span>
                      <span className="text-xs text-[#8193A8] ml-2">
                        · staffed {msg.chain?.length || 4} engineers for this
                      </span>
                    </div>
                  </div>

                  {/* Intro note if present */}
                  {msg.intro && (
                    <div className="text-sm text-[#44546A] pl-10 leading-relaxed">
                      {msg.intro}
                    </div>
                  )}

                  {/* Plan Pipeline Card if present */}
                  {msg.plan && msg.plan.length > 0 && (
                    <div className="ml-0 sm:ml-10 bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs space-y-2.5">
                      <div className="text-[11.5px] font-bold uppercase tracking-wider text-[#8193A8]">
                        Dashnode’s plan
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {msg.plan.map((stepKey, pIdx) => {
                          const eng = ENGINEER_MAP[stepKey] || {
                            name: stepKey,
                            bg: 'var(--blue-tint)',
                            fg: 'var(--blue)',
                            icon: Sparkles
                          };
                          const Icon = eng.icon;

                          return (
                            <React.Fragment key={stepKey + pIdx}>
                              {pIdx > 0 && (
                                <span className="text-[#8193A8] text-xs">→</span>
                              )}
                              <span className="inline-flex items-center gap-1.5 border border-slate-200 bg-white rounded-full px-2.5 py-1 text-xs font-medium text-[#44546A] shadow-2xs">
                                <span
                                  className="w-5 h-5 rounded-[6px] flex items-center justify-center shrink-0"
                                  style={{ backgroundColor: eng.bg, color: eng.fg }}
                                >
                                  <Icon className="w-3 h-3" />
                                </span>
                                <span>{eng.name}</span>
                              </span>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {msg.planLoop && (
                        <div className="text-xs text-[#44546A] flex items-center gap-1.5 pt-1">
                          <RotateCcw className="w-3.5 h-3.5 text-[#0C64E8] shrink-0" />
                          <span>{msg.planLoop}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assistant Markdown Content Card */}
                  <div className="ml-0 sm:ml-10 bg-white border border-slate-200 rounded-xl p-5 shadow-xs text-sm text-slate-800 leading-relaxed space-y-3">
                    <MarkdownRenderer content={msg.text} />
                  </div>

                  {/* Tied Collapsible Tool Tasks (Below Assistant Card) */}
                  {msg.tasks && msg.tasks.length > 0 && (
                    <div className="ml-0 sm:ml-10">
                      <AgentToolTasks tasks={msg.tasks} isStreaming={isGenerating} />
                    </div>
                  )}

                  {/* Deliverable Card if present */}
                  {msg.deliver && (
                    <div className="ml-0 sm:ml-10 bg-gradient-to-b from-white to-[#FBFDFF] border border-[#C6D6EE] rounded-xl p-4 sm:p-5 shadow-xs space-y-3.5">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#EFF5FE] text-[#0C64E8] flex items-center justify-center shrink-0 shadow-2xs">
                          <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <b className="text-sm font-semibold text-slate-900 block">
                            {msg.deliver.title}
                          </b>
                          <p className="text-xs text-[#44546A] mt-0.5">
                            {msg.deliver.sub}
                          </p>
                        </div>
                      </div>

                      {/* Deliverable metrics if present */}
                      {msg.deliver.metrics && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {msg.deliver.metrics.map(([mk, mv]) => (
                            <div key={mk} className="border border-slate-200 rounded-lg p-2.5 bg-white">
                              <span className="text-[11px] text-[#8193A8] block">{mk}</span>
                              <b className="text-sm font-semibold text-slate-900 mt-0.5 block font-mono">
                                {mv}
                              </b>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* File tags */}
                      {msg.deliver.files && (
                        <div className="flex flex-wrap gap-1.5">
                          {msg.deliver.files.map((f) => (
                            <span
                              key={f}
                              className="inline-flex items-center gap-1.5 text-xs text-[#44546A] bg-[#EEF2F7] rounded-md px-2.5 py-1"
                            >
                              <FileText className="w-3 h-3 text-[#8193A8]" />
                              <span>{f}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      {msg.deliver.actions && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {msg.deliver.actions.map(([kind, _actKey, label]) => {
                            const isPrimary = kind === 'primary';
                            return (
                              <button
                                key={label}
                                type="button"
                                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                                  isPrimary
                                    ? 'bg-[#0C64E8] hover:bg-[#0A4FBB] text-white shadow-xs'
                                    : 'bg-white border border-slate-200 text-[#44546A] hover:bg-[#EEF2F7]'
                                }`}
                              >
                                {isPrimary ? (
                                  <Send className="w-3.5 h-3.5" />
                                ) : (
                                  <FileText className="w-3.5 h-3.5 text-[#8193A8]" />
                                )}
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Footer guarantee note */}
                      {msg.deliver.foot && (
                        <div className="pt-2 border-t border-dashed border-slate-200 text-[11.5px] text-[#8193A8] flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-[#11A389] shrink-0" />
                          <span>{msg.deliver.foot}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* In-Flight Generator Indicator */}
            {isGenerating && (
              <div className="flex items-center gap-3 text-xs text-[#0C64E8] bg-white border border-blue-200 px-4 py-2.5 rounded-xl shadow-xs ml-0 sm:ml-10">
                <span className="w-2 h-2 rounded-full bg-[#0C64E8] animate-ping" />
                <span className="font-medium">
                  Autonomous engineering pipeline running...
                </span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 3. STICKY FOLLOW-UP COMPOSER (In Conversation Mode) */}
      {hasMessages && (
        <div className="sticky bottom-0 bg-gradient-to-t from-[#F3F6F9] via-[#F3F6F9]/95 to-transparent px-6 py-4 z-20">
          <div className="max-w-[780px] mx-auto bg-white border border-slate-200 rounded-xl shadow-[0_4px_20px_rgba(16,36,66,0.08)] p-2.5 px-3 flex items-center gap-3 focus-within:border-[#0C64E8] focus-within:ring-2 focus-within:ring-[#0C64E8]/10">
            {/* Plus button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-[#EEF2F7] flex items-center justify-center text-[#44546A] transition-colors shrink-0 cursor-pointer"
              title="Attach CAD or document"
            >
              <Plus className="w-4 h-4" />
            </button>

            {/* Attached file chip */}
            {attachedFile && (
              <div className="inline-flex items-center gap-1.5 bg-[#EFF5FE] border border-[#C6D6EE] px-2 py-0.5 rounded text-xs shrink-0">
                <span className="font-semibold text-slate-900 text-[11px] truncate max-w-[120px]">
                  {attachedFile.name}
                </span>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-slate-400 hover:text-slate-700 text-xs font-bold"
                >
                  ×
                </button>
              </div>
            )}

            {/* Input textarea */}
            <textarea
              ref={followUpTextareaRef}
              rows={1}
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask a follow-up, or change a constraint…"
              className="flex-1 text-xs sm:text-sm text-slate-900 placeholder-[#8193A8] border-none outline-none resize-none pt-1 leading-relaxed min-h-[30px]"
            />

            {/* Send button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isGenerating || (!inputText.trim() && !attachedFile)}
              className="w-[34px] h-[34px] rounded-[9px] bg-[#0C64E8] hover:bg-[#0A4FBB] disabled:opacity-40 text-white flex items-center justify-center shadow-[0_2px_6px_rgba(12,100,232,0.30)] transition-all active:scale-95 cursor-pointer shrink-0"
              title="Send message"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
